import { Box, Stack, Text } from '@mantine/core'
import type { ReactNode } from 'react'
import { DepthContour } from './DepthContour'

export type EmptyKind = 'dock' | 'lighthouse' | 'anchor' | 'sextant' | 'buoy' | 'compass' | 'lifering'

interface Props {
  kind?: EmptyKind
  title: string
  description?: string
  action?: ReactNode
  /** Show subtle depth-contour backdrop. Default true. */
  backdrop?: boolean
  size?: 'sm' | 'md' | 'lg'
}

const ICON_SIZES = { sm: 80, md: 120, lg: 160 }
const BACKDROP_SIZES = { sm: 240, md: 360, lg: 480 }

/**
 * Hand-drawn maritime line-art empty state (Pelabuhan design language).
 * Hand-drawn single-stroke aesthetic — premium feel without commissioning
 * custom illustration. Optional depth-contour backdrop adds "this is a
 * recognizable empty space, by design" cue.
 *
 * Use for: list/table empty states, no-data dashboards, "all clear" 🎉 moments.
 */
export function EmptyState({ kind = 'dock', title, description, action, backdrop = true, size = 'md' }: Props) {
  return (
    <Stack
      align="center"
      gap="md"
      py={size === 'sm' ? 'lg' : 'xl'}
      style={{ position: 'relative', overflow: 'hidden' }}
    >
      {backdrop && (
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            color: 'var(--mantine-color-laut-6)',
            opacity: 0.5,
          }}
        >
          <DepthContour size={BACKDROP_SIZES[size]} opacity={0.18} rings={6} />
        </Box>
      )}
      <Box style={{ position: 'relative', zIndex: 1 }}>
        <Illustration kind={kind} size={ICON_SIZES[size]} />
      </Box>
      <Stack gap={4} align="center" style={{ position: 'relative', zIndex: 1, maxWidth: 360 }}>
        <Text fw={600} size={size === 'sm' ? 'sm' : 'md'} ta="center" style={{ fontFamily: 'var(--font-display)' }}>
          {title}
        </Text>
        {description && (
          <Text c="dimmed" size="xs" ta="center" style={{ lineHeight: 1.5 }}>
            {description}
          </Text>
        )}
      </Stack>
      {action && (
        <Box mt="xs" style={{ position: 'relative', zIndex: 1 }}>
          {action}
        </Box>
      )}
    </Stack>
  )
}

/* Hand-drawn line-art illustrations — single stroke, current color.
   All viewBox 100×100, stroke 2 for soft hand-drawn feel. */

function Illustration({ kind, size }: { kind: EmptyKind; size: number }) {
  const baseProps = {
    width: size,
    height: size,
    viewBox: '0 0 100 100',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    style: { color: 'var(--mantine-color-laut-7)' },
    'aria-hidden': true,
  }

  switch (kind) {
    case 'dock':
      // Wooden dock with pilings extending into water
      return (
        <svg {...baseProps} aria-hidden="true">
          <line x1="10" y1="60" x2="90" y2="60" />
          <line x1="10" y1="64" x2="90" y2="64" />
          <line x1="20" y1="60" x2="20" y2="86" />
          <line x1="35" y1="60" x2="35" y2="86" />
          <line x1="50" y1="60" x2="50" y2="86" />
          <line x1="65" y1="60" x2="65" y2="86" />
          <line x1="80" y1="60" x2="80" y2="86" />
          <path d="M 5 78 Q 15 74 25 78 T 45 78 T 65 78 T 95 78" opacity="0.5" />
          <path d="M 5 90 Q 15 86 25 90 T 45 90 T 65 90 T 95 90" opacity="0.35" />
          {/* Mooring bollard */}
          <circle cx="50" cy="56" r="3" fill="currentColor" />
          <line x1="50" y1="50" x2="50" y2="56" strokeWidth={2.4} />
        </svg>
      )

    case 'lighthouse':
      return (
        <svg {...baseProps} aria-hidden="true">
          {/* Tower */}
          <path d="M 40 80 L 42 30 L 58 30 L 60 80 Z" />
          <line x1="40" y1="55" x2="60" y2="55" />
          {/* Top room */}
          <rect x="38" y="22" width="24" height="10" rx="1" />
          {/* Dome */}
          <path d="M 38 22 Q 50 12 62 22" />
          <line x1="50" y1="6" x2="50" y2="14" />
          {/* Light beams */}
          <path d="M 38 24 L 22 18" opacity="0.4" />
          <path d="M 62 24 L 78 18" opacity="0.4" />
          {/* Rocks */}
          <path d="M 22 80 Q 30 76 38 80" />
          <path d="M 62 80 Q 70 76 78 80" />
          {/* Water */}
          <path d="M 8 88 Q 20 84 32 88 T 56 88 T 80 88 T 96 88" opacity="0.5" />
        </svg>
      )

    case 'anchor':
      return (
        <svg {...baseProps} aria-hidden="true">
          <circle cx="50" cy="18" r="5" />
          <line x1="50" y1="23" x2="50" y2="82" />
          <line x1="38" y1="32" x2="62" y2="32" strokeWidth={2.4} />
          <path d="M 20 60 Q 22 80 50 82 Q 78 80 80 60" />
          <line x1="14" y1="58" x2="26" y2="62" />
          <line x1="86" y1="58" x2="74" y2="62" />
          {/* Water below */}
          <path d="M 8 92 Q 20 88 32 92 T 56 92 T 80 92 T 96 92" opacity="0.4" />
        </svg>
      )

    case 'sextant':
      return (
        <svg {...baseProps} aria-hidden="true">
          {/* Arc */}
          <path d="M 18 78 A 40 40 0 0 1 82 78" />
          <path d="M 22 78 A 36 36 0 0 1 78 78" opacity="0.6" />
          {/* Tick marks */}
          <line x1="30" y1="44" x2="32" y2="48" />
          <line x1="40" y1="35" x2="42" y2="40" />
          <line x1="50" y1="32" x2="50" y2="38" />
          <line x1="60" y1="35" x2="58" y2="40" />
          <line x1="70" y1="44" x2="68" y2="48" />
          {/* Vertical arm */}
          <line x1="50" y1="22" x2="50" y2="78" strokeWidth={2} />
          {/* Base */}
          <line x1="18" y1="78" x2="82" y2="78" strokeWidth={2.2} />
          {/* Sighting scope */}
          <circle cx="50" cy="20" r="4" />
        </svg>
      )

    case 'buoy':
      return (
        <svg {...baseProps} aria-hidden="true">
          {/* Buoy body */}
          <ellipse cx="50" cy="58" rx="20" ry="14" />
          <line x1="32" y1="58" x2="68" y2="58" opacity="0.5" />
          {/* Top spire */}
          <line x1="50" y1="22" x2="50" y2="44" />
          <path d="M 46 28 L 50 22 L 54 28 Z" fill="currentColor" />
          {/* Flag */}
          <path d="M 50 28 L 60 32 L 50 36 Z" opacity="0.6" />
          {/* Water */}
          <path d="M 8 76 Q 20 72 32 76 T 56 76 T 80 76 T 96 76" />
          <path d="M 8 86 Q 20 82 32 86 T 56 86 T 80 86 T 96 86" opacity="0.5" />
        </svg>
      )

    case 'compass':
      return (
        <svg {...baseProps} aria-hidden="true">
          <circle cx="50" cy="50" r="38" />
          <circle cx="50" cy="50" r="32" opacity="0.5" />
          {/* N arrow filled */}
          <path d="M 50 50 L 50 16 L 53 50 Z" fill="currentColor" />
          <path d="M 50 50 L 50 84 L 47 50 Z" opacity="0.5" />
          <path d="M 50 50 L 84 50 L 50 53 Z" opacity="0.5" />
          <path d="M 50 50 L 16 50 L 50 47 Z" opacity="0.5" />
          {/* Ticks */}
          <line x1="50" y1="12" x2="50" y2="18" strokeWidth={2.2} />
          <line x1="88" y1="50" x2="82" y2="50" />
          <line x1="50" y1="88" x2="50" y2="82" />
          <line x1="12" y1="50" x2="18" y2="50" />
          <circle cx="50" cy="50" r="3" fill="currentColor" />
        </svg>
      )

    case 'lifering':
      return (
        <svg {...baseProps} aria-hidden="true">
          <circle cx="50" cy="50" r="32" />
          <circle cx="50" cy="50" r="20" />
          {/* Stripes */}
          <path d="M 50 18 L 50 30" strokeWidth={2.2} />
          <path d="M 50 70 L 50 82" strokeWidth={2.2} />
          <path d="M 18 50 L 30 50" strokeWidth={2.2} />
          <path d="M 70 50 L 82 50" strokeWidth={2.2} />
          {/* Rope */}
          <path d="M 50 18 Q 32 6 18 16" opacity="0.5" />
        </svg>
      )
  }
}
